/**
 * ShopCulture Recently Viewed Products Tracker
 * Tracks and stores recently viewed products in localStorage
 * Auto-initializes on product pages
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'shopculture_recently_viewed';
  const MAX_PRODUCTS = 10;

  const RecentlyViewed = {
    /**
     * Get all recently viewed products from localStorage
     */
    getProducts: function() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('[Tracking] Error reading recently viewed products:', e);
        return [];
      }
    },

    /**
     * Save products to localStorage
     */
    saveProducts: function(products) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
      } catch (e) {
        console.error('[Tracking] Error saving recently viewed products:', e);
      }
    },

    /**
     * Add a product to recently viewed
     * @param {Object} product - Product data
     * @param {string} product.id - Product ID
     * @param {string} product.handle - Product handle
     * @param {string} product.title - Product title
     * @param {string} product.url - Product URL
     * @param {string} product.image - Product image URL
     * @param {string} product.price - Product price
     */
    addProduct: function(product) {
      if (!product || !product.id) {
        console.error('[Tracking] Invalid product data');
        return;
      }

      let products = this.getProducts();

      // Remove product if it already exists (to move it to the front)
      products = products.filter(p => p.id !== product.id);

      // Add to the beginning of the array
      products.unshift({
        id: product.id,
        handle: product.handle,
        title: product.title,
        url: product.url,
        image: product.image,
        price: product.price,
        timestamp: Date.now()
      });

      // Limit to MAX_PRODUCTS
      if (products.length > MAX_PRODUCTS) {
        products = products.slice(0, MAX_PRODUCTS);
      }

      this.saveProducts(products);
    },

    /**
     * Track current product page
     */
    trackCurrentProduct: function(retryCount = 0) {
      // Check if we're on a product page
      if (!window.ShopifyAnalytics || !window.ShopifyAnalytics.meta || !window.ShopifyAnalytics.meta.product) {
        return;
      }

      const productMeta = window.ShopifyAnalytics.meta.product;

      // Extract handle from URL
      const urlPath = window.location.pathname;
      const handleMatch = urlPath.match(/\/products\/([^?#]+)/);
      const handle = handleMatch ? handleMatch[1] : null;

      if (!handle) {
        console.error('[Tracking] Could not extract product handle from URL');
        return;
      }

      // Get image from page (more reliable than JSON endpoint)
      // Try multiple strategies to find the product image
      let pageImage = null;

      // Strategy 1: Try main product image by ID
      const mainImageById = document.getElementById('mainProductImage');
      if (mainImageById && mainImageById.src) {
        pageImage = mainImageById.src;
      }

      // Strategy 2: Try main product image by class
      if (!pageImage) {
        const mainImageByClass = document.querySelector('.shopculture-product-main__image');
        if (mainImageByClass && mainImageByClass.src) {
          pageImage = mainImageByClass.src;
        }
      }

      // Strategy 3: Try OG meta tag
      if (!pageImage) {
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage && ogImage.content) {
          pageImage = ogImage.content;
        }
      }

      // Strategy 4: Try any product image
      if (!pageImage) {
        const anyProductImage = document.querySelector('[data-product-image], .product__image, .product-image');
        if (anyProductImage && anyProductImage.src) {
          pageImage = anyProductImage.src;
        }
      }

      // If no image found and this is first attempt, retry after a short delay
      if (!pageImage && retryCount < 2) {
        setTimeout(() => {
          this.trackCurrentProduct(retryCount + 1);
        }, 300);
        return;
      }

      // Fetch product data from Shopify JSON endpoint (using locale-aware URL)
      const productUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? `${window.Shopify.routes.root}products/${handle}.js`
        : `/products/${handle}.js`;

      fetch(productUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch product data');
          }
          return response.json();
        })
        .then(productData => {
          // Build product object - prefer page image over JSON endpoint image
          const product = {
            id: productData.id || productMeta.id,
            handle: productData.handle || handle,
            title: productData.title,
            url: urlPath,
            image: pageImage || productData.featured_image || (productData.images && productData.images[0]),
            price: productData.price
          };

          // Only track if we have at least id and title
          if (product.id && product.title) {
            this.addProduct(product);
          }
        })
        .catch(error => {
          console.error('[Tracking] Error fetching product data:', error);
        });
    },

    /**
     * Clear all recently viewed products
     */
    clear: function() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.error('Error clearing recently viewed products:', e);
      }
    }
  };

  // Expose to window for global access
  window.ShopCultureRecentlyViewed = RecentlyViewed;

  // Auto-track on product pages when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      RecentlyViewed.trackCurrentProduct();
    });
  } else {
    RecentlyViewed.trackCurrentProduct();
  }
})();
